import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { DashboardLayout } from '../components/Layout';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
    Cell,
} from 'recharts';

const AdminAdvancedDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('week');
    const [activeSection, setActiveSection] = useState('overview');

    // Reports data state
    const [purchasesReport, setPurchasesReport] = useState([]);
    const [stockReport, setStockReport] = useState([]);
    const [salesReport, setSalesReport] = useState([]);
    const [membersReport, setMembersReport] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);

    // ERP dashboards for /admin/analytics
    const [erpSalesDash, setErpSalesDash] = useState(null);
    const [erpPurchDash, setErpPurchDash] = useState(null);
    const [erpStockDash, setErpStockDash] = useState(null);
    const [erpDashLoading, setErpDashLoading] = useState(false);

    // AI Financial Report
    const [financialReport, setFinancialReport] = useState(null);
    const [financialLoading, setFinancialLoading] = useState(false);
    const [financialChatInput, setFinancialChatInput] = useState('');
    const [financialChatMessages, setFinancialChatMessages] = useState([]);
    const financialChatScrollRef = React.useRef(null);

    // Members pagination (Ver mais)
    const [membersVisibleCount, setMembersVisibleCount] = useState(10);
    
    // Invoice modal state
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [newInvoice, setNewInvoice] = useState(false);

    useEffect(() => {
        fetchStats();
    }, [period]);

    const chartColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#06b6d4'];

    const ymLabel = (key) => {
        try {
            const [y, m] = String(key || '').split('-').map(Number);
            if (!y || !m) return String(key || '');
            const d = new Date(y, m - 1, 1);
            return d.toLocaleDateString('pt-PT', { month: 'short' });
        } catch {
            return String(key || '');
        }
    };

    const fetchErpDashboards = async () => {
        setErpDashLoading(true);
        try {
            const now = new Date();
            const start = new Date(now);
            start.setFullYear(now.getFullYear() - 6);
            start.setHours(0, 0, 0, 0);

            const [salesRes, purchRes, stockRes] = await Promise.all([
                api.get('/analytics/charts/sales', { params: { start: start.toISOString(), end: now.toISOString() } }).catch(() => ({ data: null })),
                api.get('/analytics/charts/purchases', { params: { start: start.toISOString(), end: now.toISOString() } }).catch(() => ({ data: null })),
                api.get('/analytics/charts/stock').catch(() => ({ data: null })),
            ]);

            setErpSalesDash(salesRes.data);
            setErpPurchDash(purchRes.data);
            setErpStockDash(stockRes.data);
        } catch {
            setErpSalesDash(null);
            setErpPurchDash(null);
            setErpStockDash(null);
        } finally {
            setErpDashLoading(false);
        }
    };

    const fetchFinancialReport = async () => {
        setFinancialLoading(true);
        try {
            const res = await api.get('/AdminAI/financial-report');
            setFinancialReport(res.data);
        } catch (error) {
            console.error('Error fetching financial report:', error);
            // toast.error('Erro ao gerar relatório financeiro');
        } finally {
            setFinancialLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const [
                dashRes,
                usersRes,
                revenueRes,
                checkInRes,
                gamificationRes
            ] = await Promise.all([
                api.get(`/dashboard/stats?period=${period}`).catch(() => ({ data: {} })),
                api.get('/users').catch(() => ({ data: [] })),
                api.get(`/credits/admin/revenue?period=${period}`).catch(() => ({ data: {} })),
                api.get('/checkin/stats').catch(() => ({ data: {} })),
                api.get('/gamification/admin/stats').catch(() => ({ data: {} }))
            ]);

            setStats({
                dashboard: dashRes.data,
                users: usersRes.data,
                revenue: revenueRes.data,
                checkIn: checkInRes.data,
                gamification: gamificationRes.data
            });
        } catch (error) {
            console.error('Erro:', error);
            toast.error('Erro ao carregar estatísticas');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-PT', {
            style: 'currency',
            currency: 'EUR'
        }).format(value || 0);
    };

    // Fetch reports data
    const fetchPurchasesReport = async () => {
        try {
            setReportsLoading(true);
            console.log('Fetching purchases from:', '/purchases/invoices?pageSize=100');
            const res = await api.get('/purchases/invoices?pageSize=100');
            console.log('Purchases API status:', res.status);
            console.log('Purchases API response:', res.data);
            const purchases = res.data?.purchases || [];
            console.log('Purchases extracted:', purchases.length, 'items');
            setPurchasesReport(Array.isArray(purchases) ? purchases : []);
        } catch (error) {
            console.error('Error fetching purchases:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            setPurchasesReport([]);
        } finally {
            setReportsLoading(false);
        }
    };

    const fetchStockReport = async () => {
        try {
            setReportsLoading(true);
            // Primeiro buscar warehouses, depois buscar stock do primeiro
            const warehousesRes = await api.get('/warehouse');
            const warehouses = warehousesRes.data || [];
            if (warehouses.length > 0) {
                const stockRes = await api.get(`/warehouse/${warehouses[0].id}/stock`);
                setStockReport(Array.isArray(stockRes.data) ? stockRes.data : []);
            } else {
                setStockReport([]);
            }
        } catch (error) {
            console.error('Error fetching stock:', error);
            setStockReport([]);
        } finally {
            setReportsLoading(false);
        }
    };

    const fetchSalesReport = async () => {
        try {
            setReportsLoading(true);
            const res = await api.get('/TokenShop/admin/purchases');
            setSalesReport(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error fetching sales:', error);
            setSalesReport([]);
        } finally {
            setReportsLoading(false);
        }
    };

    const fetchInvoiceDetails = async (invoiceId) => {
        try {
            setInvoiceLoading(true);
            const res = await api.get(`/purchases/invoices/${invoiceId}`);
            setInvoiceDetails(res.data);
            setInvoiceModalOpen(true);
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            toast.error('Erro ao carregar detalhes da fatura');
        } finally {
            setInvoiceLoading(false);
        }
    };

    const closeInvoiceModal = () => {
        setInvoiceModalOpen(false);
        setInvoiceDetails(null);
        setSelectedInvoice(null);
        setEditMode(false);
        setNewInvoice(false);
    };

    const openNewInvoiceModal = () => {
        setNewInvoice(true);
        setEditMode(true);
        setInvoiceDetails({
            invoiceNumber: '',
            invoiceDate: new Date().toISOString().split('T')[0],
            status: 'Draft',
            supplier: null,
            warehouse: null,
            notes: '',
            lines: [],
            subTotal: 0,
            taxTotal: 0,
            discountTotal: 0,
            total: 0
        });
        setInvoiceModalOpen(true);
    };

    const toggleEditMode = () => {
        setEditMode(!editMode);
    };

    const saveInvoice = async () => {
        try {
            if (newInvoice) {
                // Create new invoice
                await api.post('/purchases/invoices', invoiceDetails);
                toast.success('Fatura criada com sucesso!');
            } else {
                // Update existing invoice
                await api.put(`/purchases/invoices/${invoiceDetails.id}`, invoiceDetails);
                toast.success('Fatura atualizada com sucesso!');
            }
            fetchPurchasesReport();
            closeInvoiceModal();
        } catch (error) {
            console.error('Error saving invoice:', error);
            toast.error('Erro ao guardar fatura');
        }
    };

    const deleteInvoice = async () => {
        if (!window.confirm('Tem certeza que deseja anular esta fatura?')) return;
        try {
            await api.delete(`/purchases/invoices/${invoiceDetails.id}`);
            toast.success('Fatura anulada com sucesso!');
            fetchPurchasesReport();
            closeInvoiceModal();
        } catch (error) {
            console.error('Error deleting invoice:', error);
            toast.error('Erro ao anular fatura');
        }
    };

    const duplicateInvoice = () => {
        setNewInvoice(true);
        setEditMode(true);
        setInvoiceDetails({
            ...invoiceDetails,
            id: null,
            invoiceNumber: '',
            invoiceDate: new Date().toISOString().split('T')[0],
            status: 'Draft'
        });
        toast.info('Fatura duplicada. Altere os dados e grave.');
    };

    const handleFinancialQuery = async (query) => {
        if(!query) return;
        setFinancialLoading(true);
        try {
            // Optimistic append (WhatsApp style)
            setFinancialChatMessages(prev => ([
                ...prev,
                { id: Date.now() + '-u', role: 'user', text: query, documents: [] }
            ]));

            const res = await api.post('/AdminAI/financial-chat', {
                query: query,
                conversationId: 'admin-advanced-dashboard'
            });

            setFinancialReport(prev => {
                const base = prev || {
                    projectedMonthlyRevenue: 0,
                    revenueAtRisk: 0,
                    highRiskCustomers: 0,
                    executiveSummary: ''
                };

                const nextMetrics = {
                    projectedMonthlyRevenue: res.data.metrics?.projectedRevenue || base.projectedMonthlyRevenue || 0,
                    revenueAtRisk: res.data.metrics?.revenueAtRisk || base.revenueAtRisk || 0,
                    highRiskCustomers: res.data.metrics?.highRiskCount || base.highRiskCustomers || 0,
                };

                const entry = `📝 **Questão:** ${query}\n\n🤖 **Resposta:** ${res.data.response}`;
                const prevText = base.executiveSummary || '';
                const executiveSummary = !prevText.trim()
                    ? entry
                    : `${prevText}\n\n---\n\n${entry}`;

                return {
                    ...base,
                    ...nextMetrics,
                    executiveSummary,
                };
            });

            setFinancialChatMessages(prev => ([
                ...prev,
                {
                    id: Date.now() + '-a',
                    role: 'assistant',
                    text: res.data.response,
                    documents: Array.isArray(res.data.documents) ? res.data.documents : []
                }
            ]));
            
        } catch (error) {
            console.error('Error in financial chat:', error);
            toast.error('Não consegui analisar essa questão.');
        } finally {
            setFinancialLoading(false);
        }
    };

    const sendFinancialChat = async () => {
        const val = (financialChatInput || '').trim();
        if (!val) return;
        setFinancialChatInput('');
        await handleFinancialQuery(val);
    };

    useEffect(() => {
        // Auto-scroll to bottom on new messages
        try {
            if (financialChatScrollRef.current) {
                financialChatScrollRef.current.scrollTop = financialChatScrollRef.current.scrollHeight;
            }
        } catch {
            // ignore
        }
    }, [financialChatMessages.length, financialLoading]);

    const fetchMembersReport = async () => {
        try {
            setReportsLoading(true);
            const res = await api.get('/users');
            setMembersReport(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Error fetching members:', error);
            setMembersReport([]);
        } finally {
            setReportsLoading(false);
        }
    };

    // Load report data when section changes
    useEffect(() => {
        if (activeSection === 'purchases') {
            if (!erpPurchDash || !erpStockDash) fetchErpDashboards();
        } else if (activeSection === 'members' && membersReport.length === 0) {
            fetchMembersReport();
            setMembersVisibleCount(10);
        } else if (activeSection === 'revenue' && salesReport.length === 0) {
            fetchSalesReport();
        } else if (activeSection === 'billing') {
            if (!erpSalesDash) fetchErpDashboards();
            // Automatically fetch if enabled or just let user button do it
        }
    }, [activeSection]);

    if (loading) {
        return (
            <DashboardLayout userType="admin">
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>A carregar dashboard...</p>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userType="admin">
            <div style={styles.container}>
                {/* Header */}
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>📊 Dashboard Avançado</h1>
                        <p style={styles.subtitle}>Visão completa do seu ginásio</p>
                    </div>
                    <div style={styles.periodSelector}>
                        {['day', 'week', 'month', 'year'].map(p => (
                            <button
                                key={p}
                                style={{
                                    ...styles.periodBtn,
                                ...(period === p ? styles.periodBtnActive : {})
                            }}
                            onClick={() => setPeriod(p)}
                        >
                            {p === 'day' ? 'Hoje' : p === 'week' ? 'Semana' : p === 'month' ? 'Mês' : 'Ano'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Navigation */}
            <div style={styles.nav}>
                {[
                    { id: 'overview', icon: '📈', label: 'Geral' },
                    { id: 'members', icon: '👥', label: 'Membros' },
                    { id: 'classes', icon: '📅', label: 'Aulas' },
                    { id: 'revenue', icon: '💰', label: 'Receita' },
                    { id: 'engagement', icon: '🎮', label: 'Engagement' },
                    { id: 'billing', icon: '🧾', label: 'Faturação' },
                    { id: 'purchases', icon: '📦', label: 'Compras & Stock' },
                ].map(item => (
                    <button
                        key={item.id}
                        style={{
                            ...styles.navBtn,
                            ...(activeSection === item.id ? styles.navBtnActive : {})
                        }}
                        onClick={() => setActiveSection(item.id)}
                    >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                    </button>
                ))}
            </div>

            <div style={styles.content}>
                {/* Overview Section */}
                {activeSection === 'overview' && (
                    <>
                        {/* KPI Cards */}
                        <div style={styles.kpiGrid}>
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiIcon}>👥</div>
                                <div style={styles.kpiInfo}>
                                    <span style={styles.kpiLabel}>Membros Ativos</span>
                                    <span style={styles.kpiValue}>{(Array.isArray(stats?.users) ? stats.users.filter(u => u.isActive !== false).length : 0)}</span>
                                    <span style={styles.kpiChange}>de {(Array.isArray(stats?.users) ? stats.users.length : 0)} registados</span>
                                </div>
                            </div>
                            
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiIcon}>📅</div>
                                <div style={styles.kpiInfo}>
                                    <span style={styles.kpiLabel}>Reservas</span>
                                    <span style={styles.kpiValue}>{stats?.dashboard?.bookings?.total || 0}</span>
                                    <span style={styles.kpiChange}>No Período selecionado</span>
                                </div>
                            </div>
                            
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiIcon}>✅</div>
                                <div style={styles.kpiInfo}>
                                    <span style={styles.kpiLabel}>Check-ins</span>
                                    <span style={styles.kpiValue}>{stats?.checkIn?.summary?.totalCheckIns || 0}</span>
                                    <span style={styles.kpiChange}>Taxa: {stats?.checkIn?.summary?.checkInRate || '0%'}</span>
                                </div>
                            </div>
                            
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiIcon}>💰</div>
                                <div style={styles.kpiInfo}>
                                    <span style={styles.kpiLabel}>Receita</span>
                                    <span style={styles.kpiValue}>{formatCurrency(stats?.dashboard?.revenue?.total || 0)}</span>
                                    <span style={styles.kpiChange}>No Período selecionado</span>
                                </div>
                            </div>
                        </div>

                        {/* Charts Row */}
                        <div style={styles.chartsRow}>
                            <div style={styles.chartCard}>
                                <h3>📈 Reservas por Dia</h3>
                                <div style={styles.chartPlaceholder}>
                                    <div style={styles.barChart}>
                                        {(stats?.dashboard?.charts?.bookingsByDay || []).slice(-7).map((day, i) => {
                                            const maxCount = Math.max(...(stats?.dashboard?.charts?.bookingsByDay || []).map(d => d.count || 0), 1);
                                            return (
                                                <div key={i} style={styles.barItem}>
                                                    <div 
                                                        style={{
                                                            ...styles.bar,
                                                            height: `${(day.count / maxCount) * 100}%`
                                                        }}
                                                    ></div>
                                                    <span style={{ fontSize: '0.625rem' }}>{new Date(day.date).toLocaleDateString('pt-PT', { weekday: 'short' })}</span>
                                                    <span style={{ fontSize: '0.625rem', fontWeight: '600' }}>{day.count}</span>
                                                </div>
                                            );
                                        })}
                                        {(!stats?.dashboard?.charts?.bookingsByDay || stats.dashboard.charts.bookingsByDay.length === 0) && (
                                            <div style={{ textAlign: 'center', color: 'var(--text-muted)', width: '100%', padding: '2rem' }}>
                                                Sem dados de reservas no Período
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div style={styles.chartCard}>
                                <h3>⏰ Horários Mais Populares</h3>
                                <div style={styles.timeHeatmap}>
                                    {(stats?.checkIn?.byHour || []).filter(h => h.count > 0).slice(0, 12).map(hour => {
                                        const maxH = Math.max(...(stats?.checkIn?.byHour || []).map(h => h.count || 0), 1);
                                        return (
                                            <div 
                                                key={hour.hour}
                                                style={{
                                                    ...styles.heatCell,
                                                    backgroundColor: `rgba(99, 102, 241, ${0.2 + (hour.count / maxH) * 0.8})`,
                                                    color: (0.2 + (hour.count / maxH) * 0.8) > 0.6 ? '#fff' : 'var(--text-primary)'
                                                }}
                                            >
                                                {hour.hour}h
                                            </div>
                                        );
                                    })}
                                    {(!stats?.checkIn?.byHour || stats.checkIn.byHour.filter(h => h.count > 0).length === 0) && 
                                        [7, 8, 9, 10, 11, 12, 17, 18, 19, 20, 21].map(h => (
                                            <div key={h} style={{ ...styles.heatCell, backgroundColor: 'rgba(99, 102, 241, 0.15)', color: 'var(--text-primary)' }}>{h}h</div>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div style={styles.quickStats}>
                            <div style={styles.quickStatItem}>
                                <span style={styles.quickStatValue}>{stats?.dashboard?.bookings?.completed || 0}</span>
                                <span style={styles.quickStatLabel}>✅ Completadas</span>
                            </div>
                            <div style={styles.quickStatItem}>
                                <span style={styles.quickStatValue}>{stats?.dashboard?.bookings?.cancelled || 0}</span>
                                <span style={styles.quickStatLabel}>✌ Canceladas</span>
                            </div>
                            <div style={styles.quickStatItem}>
                                <span style={styles.quickStatValue}>{stats?.dashboard?.bookings?.pending || 0}</span>
                                <span style={styles.quickStatLabel}>⏳ Pendentes</span>
                            </div>
                            <div style={styles.quickStatItem}>
                                <span style={styles.quickStatValue}>{stats?.gamification?.totalXp || 0}</span>
                                <span style={styles.quickStatLabel}>🏆 XP Total</span>
                            </div>
                        </div>

                        {/* Members list (requested on Geral) */}
                        <div style={styles.chartCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
                                <h3 style={{ margin: 0 }}>👥 Membros recentes</h3>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button
                                        onClick={() => setActiveSection('members')}
                                        style={{ background: 'var(--bg-card)', color: 'var(--info-text)', border: '1px solid var(--border-color)', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 13 }}
                                    >
                                        Ver todos
                                    </button>
                                    <button
                                        onClick={() => setMembersVisibleCount(v => v + 10)}
                                        disabled={membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0)}
                                        style={{
                                            background: membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0) ? '#e5e7eb' : '#3b82f6',
                                            color: membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0) ? '#6b7280' : '#fff',
                                            border: 'none',
                                            padding: '8px 12px',
                                            borderRadius: 8,
                                            cursor: membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0) ? 'not-allowed' : 'pointer',
                                            fontWeight: 800,
                                            fontSize: 13,
                                        }}
                                    >
                                        Ver mais +10
                                    </button>
                                </div>
                            </div>

                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                                Mostra os últimos {Math.min(membersVisibleCount, (Array.isArray(stats?.users) ? stats.users.length : 0))} registos.
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                            <th style={{ padding: 10, textAlign: 'left', fontWeight: 700 }}>Nome</th>
                                            <th style={{ padding: 10, textAlign: 'left', fontWeight: 700 }}>Email</th>
                                            <th style={{ padding: 10, textAlign: 'right', fontWeight: 700 }}>Créditos</th>
                                            <th style={{ padding: 10, textAlign: 'center', fontWeight: 700 }}>Data</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(Array.isArray(stats?.users) ? stats.users : [])
                                            .slice()
                                            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                                            .slice(0, membersVisibleCount)
                                            .map((u, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                    <td style={{ padding: 10, fontWeight: 800 }}>{u.name || '—'}</td>
                                                    <td style={{ padding: 10, color: 'var(--text-secondary)', fontSize: 13 }}>{u.email || '—'}</td>
                                                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 800, color: (u.creditsBalance || 0) > 0 ? '#10b981' : '#ef4444' }}>{u.creditsBalance || 0}</td>
                                                    <td style={{ padding: 10, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString('pt-PT') : '—'}</td>
                                                </tr>
                                            ))}
                                        {(!Array.isArray(stats?.users) || stats.users.length === 0) && (
                                            <tr>
                                                <td colSpan={4} style={{ textAlign: 'center', padding: 18, color: 'var(--text-muted)' }}>Sem membros.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Members Section */}
                {activeSection === 'members' && (
                    <>
                        <div style={styles.sectionTitle}>👥 Análise de Membros</div>
                        
                        <div style={styles.membersGrid}>
                            <div style={styles.memberCard}>
                                <h4>Total de Membros</h4>
                                <div style={styles.bigNumber}>{(Array.isArray(stats?.users) ? stats.users.length : 0)}</div>
                                <p>Utilizadores registados</p>
                            </div>
                            
                            <div style={styles.memberCard}>
                                <h4>Membros Ativos</h4>
                                <div style={styles.bigNumber}>{(Array.isArray(stats?.users) ? stats.users.filter(u => u.isActive !== false).length : 0)}</div>
                                <p>Com conta ativa</p>
                            </div>
                            
                            <div style={styles.memberCard}>
                                <h4>Novos no Período</h4>
                                <div style={styles.bigNumber}>0</div>
                                <p>Novos registos</p>
                            </div>
                        </div>

                        <div style={styles.chartCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3>📋 Membros (últimos {Math.min(membersVisibleCount, (Array.isArray(stats?.users) ? stats.users.length : 0))} de {(Array.isArray(stats?.users) ? stats.users.length : 0)})</h3>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 12px 0' }}>
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Mostra 10 de cada vez — clique “Ver mais” para carregar mais 10.</div>
                                <button
                                    onClick={() => setMembersVisibleCount(v => v + 10)}
                                    disabled={membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0)}
                                    style={{
                                        background: membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0) ? '#e5e7eb' : '#3b82f6',
                                        color: membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0) ? '#6b7280' : '#fff',
                                        border: 'none',
                                        padding: '8px 14px',
                                        borderRadius: '6px',
                                        cursor: membersVisibleCount >= (Array.isArray(stats?.users) ? stats.users.length : 0) ? 'not-allowed' : 'pointer',
                                        fontWeight: '700',
                                        fontSize: '13px'
                                    }}
                                >
                                    Ver mais +10
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Nome</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Email</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Telefone</th>
                                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Função</th>
                                            <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Créditos</th>
                                            <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Data Registo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(Array.isArray(stats?.users) ? stats.users : [])
                                            .slice()
                                            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                                            .slice(0, membersVisibleCount)
                                            .map((user, i) => (
                                            <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '12px' }}>
                                                    <strong>{user.name || 'N/A'}</strong>
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                                    {user.email || 'N/A'}
                                                </td>
                                                <td style={{ padding: '12px', fontSize: '0.875rem' }}>
                                                    {user.phone || '-'}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{ 
                                                        padding: '4px 12px', 
                                                        borderRadius: '12px', 
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        background: user.role === 'Admin' ? '#dbeafe' : '#f3f4f6',
                                                        color: user.role === 'Admin' ? '#1e40af' : '#6b7280'
                                                    }}>
                                                        {user.role || 'User'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: (user.creditsBalance || 0) > 0 ? '#10b981' : '#ef4444' }}>
                                                    {user.creditsBalance || 0}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-PT') : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!Array.isArray(stats?.users) || stats.users.length === 0) && (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                                                    Nenhum membro encontrado
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Classes Section */}
                {activeSection === 'classes' && (
                    <>
                        <div style={styles.sectionTitle}>📅 Performance de Aulas</div>
                        
                        <div style={styles.classesStats}>
                            <div style={styles.classStat}>
                                <span>🎯</span>
                                <div>
                                    <strong>Total de Reservas</strong>
                                    <span>{stats?.dashboard?.bookings?.total || 0}</span>
                                </div>
                            </div>
                            <div style={styles.classStat}>
                                <span>✅</span>
                                <div>
                                    <strong>Completadas</strong>
                                    <span>{stats?.dashboard?.bookings?.completed || 0}</span>
                                </div>
                            </div>
                            <div style={styles.classStat}>
                                <span>✌</span>
                                <div>
                                    <strong>Canceladas</strong>
                                    <span>{stats?.dashboard?.bookings?.cancelled || 0}</span>
                                </div>
                            </div>
                        </div>

                        <div style={styles.chartCard}>
                            <h3>📊 Estatísticas de Reservas</h3>
                            <div style={{ padding: '20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div style={{ padding: '1rem', background: 'var(--success-bg)', borderRadius: '8px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success-text)' }}>{stats?.dashboard?.classes?.avgOccupancy || 0}%</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Taxa Ocupação</div>
                                    </div>
                                    <div style={{ padding: '1rem', background: 'var(--info-bg)', borderRadius: '8px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--info)' }}>{stats?.dashboard?.classes?.totalSlots || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Aulas no Período</div>
                                    </div>
                                </div>
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '0.875rem' }}>
                                    Dados reais atualizados — Período: {period === 'day' ? 'Hoje' : period === 'week' ? 'Última Semana' : period === 'month' ? 'Último Mês' : 'Último Ano'}
                                </p>
                            </div>
                        </div>
                    </>
                )}

                {/* Revenue Section */}
                {activeSection === 'revenue' && (
                    <>
                        <div style={styles.sectionTitle}>💰 Análise de Receita</div>
                        
                        <div style={styles.revenueGrid}>
                            <div style={styles.revenueCard}>
                                <span>💳</span>
                                <h4>Receita Total</h4>
                                <div style={styles.revenueBig}>{formatCurrency(stats?.revenue?.totalRevenue || 0)}</div>
                                <span style={styles.revenueChange}>No Período selecionado</span>
                            </div>
                            
                            <div style={styles.revenueCard}>
                                <span>🔄</span>
                                <h4>Subscrições (MRR)</h4>
                                <div style={styles.revenueBig}>{formatCurrency(stats?.revenue?.subscriptions?.mrr || 0)}</div>
                                <span style={styles.revenueChange}>{stats?.revenue?.subscriptions?.active || 0} ativas</span>
                            </div>
                            
                            <div style={styles.revenueCard}>
                                <span>🎫</span>
                                <h4>Créditos Vendidos</h4>
                                <div style={styles.revenueBig}>{stats?.revenue?.totalCreditsSold || 0}</div>
                                <span style={styles.revenueChange}>{stats?.revenue?.totalTransactions || 0} transações</span>
                            </div>
                        </div>

                        <div style={styles.chartCard}>
                            <h3>📊 Breakdown de Receita</h3>
                            <div style={styles.revenueBreakdown}>
                                {(stats?.revenue?.revenueByMethod || []).map((item, i) => {
                                    const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                                    return (
                                        <div key={i} style={styles.breakdownItem}>
                                            <div style={styles.breakdownInfo}>
                                                <span style={{ ...styles.breakdownDot, backgroundColor: colors[i % colors.length] }}></span>
                                                <span>{item.method || 'Outro'}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <span style={styles.breakdownValue}>{formatCurrency(item.total)}</span>
                                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.count} transações</span>
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!stats?.revenue?.revenueByMethod || stats.revenue.revenueByMethod.length === 0) && (
                                    <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Sem dados de receita no Período</div>
                                )}
                            </div>
                        </div>

                        {/* Top Pacotes */}
                        {stats?.revenue?.topPackages?.length > 0 && (
                            <div style={styles.chartCard}>
                                <h3>🏆 Top Pacotes Vendidos</h3>
                                <div style={{ marginTop: '0.5rem' }}>
                                    {stats.revenue.topPackages.map((pkg, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--info)' }}>
                                            <div>
                                                <span style={{ fontWeight: '600' }}>{i + 1}. {pkg.packageName}</span>
                                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pkg.totalSold} vendas</span>
                                            </div>
                                            <span style={{ fontWeight: '700', color: 'var(--success)' }}>{formatCurrency(pkg.totalRevenue)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Faturação (ERP) — charts (detailed reports are in Relatórios PRO) */}
                {activeSection === 'billing' && (
                    <>
                        <div style={styles.sectionTitle}>🧾 Faturação & Previsão IA</div>

                        {/* WIDGET FINANCEIRO IA */}
                        <div style={{ ...styles.chartCard, marginBottom: '20px', borderLeft: '4px solid var(--accent-primary)', background: 'var(--info-bg)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.25rem', color: 'var(--accent-primary)', marginBottom: '0.25rem' }}>🤖 Consultor Financeiro IA</h3>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Pergunte sobre vendas, compras, devedores e estatísticas.</p>
                                </div>
                            </div>
                            
                            {/* Chat Interface */}
                            <div style={{ background: 'white', border: '1px solid var(--info)', borderRadius: '8px', overflow: 'hidden' }}>
                                <div ref={financialChatScrollRef} style={{ height: '320px', padding: '1rem', overflowY: 'auto', background: 'var(--primary-light)' }}>
                                    {financialChatMessages.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '4rem' }}>
                                            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>💬</div>
                                            <p style={{ margin: 0, fontWeight: 700 }}>Perguntas rápidas:</p>
                                            <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                                                {[
                                                    'Quem é o meu melhor cliente?',
                                                    'Qual o cliente com maior valor pendente?',
                                                    'Top 5 fornecedores',
                                                    'Qual a compra com maior valor?'
                                                ].map((q) => (
                                                    <button
                                                        key={q}
                                                        style={{
                                                            background: 'var(--bg-card)',
                                                            border: '1px solid var(--border-color)',
                                                            borderRadius: 12,
                                                            padding: '10px 12px',
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            fontWeight: 700,
                                                            color: 'var(--info-text)'
                                                        }}
                                                        onClick={() => handleFinancialQuery(q)}
                                                        disabled={financialLoading}
                                                    >
                                                        {q}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                            {financialChatMessages.map((m) => {
                                                const isUser = m.role === 'user';
                                                return (
                                                    <div key={m.id} style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
                                                        <div style={{
                                                            maxWidth: '82%',
                                                            background: isUser ? '#22c55e' : '#ffffff',
                                                            color: isUser ? '#ffffff' : '#111827',
                                                            borderRadius: 16,
                                                            padding: '10px 12px',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                                            border: isUser ? 'none' : '1px solid var(--border-color)'
                                                        }}>
                                                            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.95rem' }}>{m.text}</div>
                                                            {!isUser && Array.isArray(m.documents) && m.documents.length > 0 && (
                                                                <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 800 }}>📎 Documentos</div>
                                                                    {m.documents.map((d, idx) => (
                                                                        <div key={d.id || idx} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--info-text)' }}>{d.label || d.kind || 'Documento'}</span>
                                                                            {d.uiUrl && (
                                                                                <button
                                                                                    style={{ background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                                                                    onClick={() => window.open(d.uiUrl, '_blank')}
                                                                                >
                                                                                    Abrir
                                                                                </button>
                                                                            )}
                                                                            {d.pdfUrl && (
                                                                                <button
                                                                                    style={{ background: 'var(--info)', color: '#fff', border: 'none', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 900 }}
                                                                                    onClick={() => window.open(d.pdfUrl, '_blank')}
                                                                                >
                                                                                    PDF
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {financialLoading && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                    <div style={{
                                                        background: 'var(--bg-card)',
                                                        borderRadius: 16,
                                                        padding: '10px 12px',
                                                        border: '1px solid var(--border-color)',
                                                        color: 'var(--text-secondary)',
                                                        fontWeight: 800
                                                    }}>
                                                        A analisar…
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div style={{ padding: '0.75rem', borderTop: '1px solid var(--info)', background: 'white', display: 'flex', gap: '0.5rem' }}>
                                    <input
                                        type="text"
                                        placeholder="Ex: Qual o total de vendas desta semana?"
                                        style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--info)', outline: 'none' }}
                                        value={financialChatInput}
                                        onChange={(e) => setFinancialChatInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                sendFinancialChat();
                                            }
                                        }}
                                        disabled={financialLoading}
                                    />
                                    <button
                                        style={{
                                            padding: '0 1.5rem',
                                            background: financialLoading ? '#a78bfa' : '#8b5cf6',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontWeight: 'bold',
                                            cursor: financialLoading ? 'not-allowed' : 'pointer',
                                            opacity: financialLoading ? 0.8 : 1,
                                        }}
                                        onClick={sendFinancialChat}
                                        disabled={financialLoading}
                                    >
                                        {financialLoading ? 'A enviar…' : 'Enviar'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                            <button
                                onClick={() => window.location.href = '/admin/analytics-pro'}
                                style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 800 }}
                            >
                                📊 Abrir Relatórios PRO
                            </button>
                            <button
                                onClick={fetchErpDashboards}
                                style={{ background: 'var(--bg-card)', color: 'var(--info-text)', border: '1px solid var(--border-color)', padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 800 }}
                            >
                                🔄 Atualizar
                            </button>
                        </div>

                        {erpDashLoading ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>⏳ A carregar gráficos…</div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14 }}>
                                <div style={styles.chartCard}>
                                    <h3>Total Vendas por Mês</h3>
                                    <div style={{ width: '100%', height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={(erpSalesDash?.byMonth || []).slice(-12).map(x => ({ name: ymLabel(x.key), total: x.total }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                                <Bar dataKey="total" fill="#93c5fd" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div style={styles.chartCard}>
                                    <h3>Total Vendas por Família (Global)</h3>
                                    <div style={{ width: '100%', height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                                <Legend />
                                                <Pie data={(erpSalesDash?.byFamily || []).map(x => ({ name: x.family, value: x.total }))} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} label>
                                                    {(erpSalesDash?.byFamily || []).map((_, i) => (
                                                        <Cell key={i} fill={chartColors[i % chartColors.length]} />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div style={styles.chartCard}>
                                    <h3>Top Clientes Devedores</h3>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                                    <th style={{ padding: 10, textAlign: 'left' }}>Nome</th>
                                                    <th style={{ padding: 10, textAlign: 'right' }}>Pendente</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(erpSalesDash?.topDebtors || []).map((x, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                        <td style={{ padding: 10 }}>{x.clientName}</td>
                                                        <td style={{ padding: 10, textAlign: 'right', fontWeight: 800, color: 'var(--error)' }}>{formatCurrency(x.pending)}</td>
                                                    </tr>
                                                ))}
                                                {(!erpSalesDash?.topDebtors || erpSalesDash.topDebtors.length === 0) && (
                                                    <tr><td colSpan={2} style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)' }}>Sem pendentes.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={styles.chartCard}>
                                    <h3>Total Vendas por Ano</h3>
                                    <div style={{ width: '100%', height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={(erpSalesDash?.byYear || []).map(x => ({ name: String(x.year), total: x.total }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                                <Bar dataKey="total" fill="#3b82f6" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div style={styles.chartCard}>
                                    <h3>Total Vendas por Vendedor</h3>
                                    <div style={{ width: '100%', height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={(erpSalesDash?.byVendor || []).map(x => ({ name: x.vendor, total: x.total }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" hide />
                                                <YAxis />
                                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                                <Bar dataKey="total" fill="#10b981" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div style={styles.chartCard}>
                                    <h3>Top Clientes</h3>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                                    <th style={{ padding: 10, textAlign: 'left' }}>Nome</th>
                                                    <th style={{ padding: 10, textAlign: 'right' }}>Total</th>
                                                    <th style={{ padding: 10, textAlign: 'right' }}>Pendente</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(erpSalesDash?.topClients || []).map((x, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                        <td style={{ padding: 10 }}>{x.clientName}</td>
                                                        <td style={{ padding: 10, textAlign: 'right', fontWeight: 800 }}>{formatCurrency(x.total)}</td>
                                                        <td style={{ padding: 10, textAlign: 'right', fontWeight: 800, color: x.pending > 0 ? '#ef4444' : '#10b981' }}>{formatCurrency(x.pending)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={{ ...styles.chartCard, gridColumn: '1 / -1' }}>
                                    <h3>Total Vendas Mensais/Ano</h3>
                                    <div style={{ width: '100%', height: 260 }}>
                                        {(() => {
                                            const points = (erpSalesDash?.byMonth || []).map(x => ({ key: x.key, year: x.year, total: x.total }));
                                            const years = Array.from(new Set(points.map(p => p.year))).sort((a, b) => a - b).slice(-3);
                                            const months = Array.from(new Set(points.map(p => p.key))).sort().slice(-12);
                                            const data = months.map(k => {
                                                const row = { key: k, name: ymLabel(k) };
                                                years.forEach(y => {
                                                    const p = points.find(pp => pp.key === k && pp.year === y);
                                                    row[String(y)] = p ? p.total : 0;
                                                });
                                                return row;
                                            });
                                            return (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={data}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" />
                                                        <YAxis />
                                                        <Tooltip formatter={(v) => formatCurrency(v)} />
                                                        <Legend />
                                                        {years.map((y, i) => (
                                                            <Line key={y} type="monotone" dataKey={String(y)} stroke={chartColors[i % chartColors.length]} strokeWidth={3} dot={false} />
                                                        ))}
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeSection === 'engagement' && (
                    <>
                        <div style={styles.sectionTitle}>🎮 Engagement & Gamificação</div>
                        
                        <div style={styles.engagementGrid}>
                            <div style={styles.engagementCard}>
                                <span>🏆</span>
                                <div>
                                    <strong>XP Total</strong>
                                    <span style={styles.engagementValue}>{stats?.gamification?.totalXp || 0}</span>
                                </div>
                            </div>
                            
                            <div style={styles.engagementCard}>
                                <span>🔥</span>
                                <div>
                                    <strong>Badges Ganhos</strong>
                                    <span style={styles.engagementValue}>{stats?.gamification?.totalBadgesEarned || 0}</span>
                                </div>
                            </div>
                            
                            <div style={styles.engagementCard}>
                                <span>⚔️</span>
                                <div>
                                    <strong>Desafios Ativos</strong>
                                    <span style={styles.engagementValue}>{stats?.gamification?.activeChallenges || 0}</span>
                                </div>
                            </div>
                            
                            <div style={styles.engagementCard}>
                                <span>👥</span>
                                <div>
                                    <strong>Utilizadores c/ Stats</strong>
                                    <span style={styles.engagementValue}>{stats?.gamification?.totalUsers || 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* Top Users Gamification */}
                        {stats?.gamification?.topUsers?.length > 0 && (
                            <div style={styles.chartCard}>
                                <h3>🥇 Top Utilizadores - Gamificação</h3>
                                <div style={{ marginTop: '0.5rem' }}>
                                    {stats.gamification.topUsers.map((user, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--info)', gap: '0.75rem' }}>
                                            <span style={{ fontWeight: '700', color: 'var(--accent-primary)', fontSize: '1.25rem', width: '30px' }}>
                                                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}
                                            </span>
                                            <div style={{ flex: 1 }}>
                                                <strong>{user.userName || 'Utilizador'}</strong>
                                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Nível {user.level} • {user.badgesCount} badges</span>
                                            </div>
                                            <span style={{ fontWeight: '700', color: 'var(--accent-primary)' }}>{user.totalXp} XP</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={styles.chartCard}>
                            <h3>📊 Resumo Gamificação</h3>
                            <div style={{ padding: '20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div style={{ padding: '1rem', background: 'var(--primary-light)', borderRadius: '8px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--accent-primary)' }}>{stats?.gamification?.totalBadges || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Badges Disponíveis</div>
                                    </div>
                                    <div style={{ padding: '1rem', background: 'var(--warning-bg)', borderRadius: '8px', textAlign: 'center' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--warning)' }}>{stats?.gamification?.totalChallengeParticipants || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Participantes em Desafios</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', marginTop: '1rem', padding: '0.75rem', background: 'var(--info-bg)', borderRadius: '8px' }}>
                                    <span style={{ fontSize: '0.875rem', color: 'var(--info-text)' }}>Nível Médio: <strong>{stats?.gamification?.avgLevel || 0}</strong></span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Purchases & Stock Section */}
            {activeSection === 'purchases' && (
                <>
                    <h2 style={styles.sectionTitle}>📦 Relatórios de Compras, Stock & Vendas</h2>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                        <button
                            onClick={() => window.location.href = '/admin/analytics-pro'}
                            style={{ background: 'var(--info)', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 800 }}
                        >
                            📊 Abrir Relatórios PRO
                        </button>
                        <button
                            onClick={fetchErpDashboards}
                            style={{ background: 'var(--bg-card)', color: 'var(--info-text)', border: '1px solid var(--border-color)', padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 800 }}
                        >
                            🔄 Atualizar Gráficos
                        </button>
                    </div>

                    {erpDashLoading ? (
                        <div style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--text-muted)' }}>⏳ A carregar gráficos de Compras/Stock…</div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginBottom: 18 }}>
                            <div style={styles.chartCard}>
                                <h3>Compras por Mês</h3>
                                <div style={{ width: '100%', height: 220 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={(erpPurchDash?.byMonth || []).slice(-12).map(x => ({ name: ymLabel(x.key), total: x.total }))}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip formatter={(v) => formatCurrency(v)} />
                                            <Bar dataKey="total" fill="#86efac" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div style={styles.chartCard}>
                                <h3>Compras por Ano</h3>
                                <div style={{ width: '100%', height: 220 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={(erpPurchDash?.byYear || []).map(x => ({ name: String(x.year), total: x.total }))}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip formatter={(v) => formatCurrency(v)} />
                                            <Bar dataKey="total" fill="#16a34a" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div style={styles.chartCard}>
                                <h3>Compras por Fornecedor (Top)</h3>
                                <div style={{ width: '100%', height: 220 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={(erpPurchDash?.bySupplier || []).map(x => ({ name: x.supplier, total: x.total }))}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" hide />
                                            <YAxis />
                                            <Tooltip formatter={(v) => formatCurrency(v)} />
                                            <Bar dataKey="total" fill="#22c55e" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div style={styles.chartCard}>
                                <h3>Compras por Estado</h3>
                                <div style={{ width: '100%', height: 220 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Tooltip formatter={(v) => formatCurrency(v)} />
                                            <Legend />
                                            <Pie data={(erpPurchDash?.status || []).map(x => ({ name: x.status, value: x.total }))} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} label>
                                                {(erpPurchDash?.status || []).map((_, i) => (
                                                    <Cell key={i} fill={chartColors[i % chartColors.length]} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div style={styles.chartCard}>
                                <h3>Stock por Família (Qtd.)</h3>
                                <div style={{ width: '100%', height: 220 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={(erpStockDash?.byFamilyQty || []).map(x => ({ name: x.family, qty: x.qty }))}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" hide />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="qty" fill="#60a5fa" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div style={styles.chartCard}>
                                <h3>Stock Baixo (Top)</h3>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                                <th style={{ padding: 10, textAlign: 'left' }}>Artigo</th>
                                                <th style={{ padding: 10, textAlign: 'right' }}>Atual</th>
                                                <th style={{ padding: 10, textAlign: 'right' }}>Mín.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(erpStockDash?.lowStock || []).map((x, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                    <td style={{ padding: 10 }}>
                                                        <div style={{ fontWeight: 800 }}>{x.reference || '—'}</div>
                                                        <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{x.description || ''}</div>
                                                    </td>
                                                    <td style={{ padding: 10, textAlign: 'right', fontWeight: 800, color: 'var(--error)' }}>{x.currentStock}</td>
                                                    <td style={{ padding: 10, textAlign: 'right' }}>{x.minimumStock}</td>
                                                </tr>
                                            ))}
                                            {(!erpStockDash?.lowStock || erpStockDash.lowStock.length === 0) && (
                                                <tr><td colSpan={3} style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)' }}>Sem stock baixo.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
            </div>

            {/* Invoice Details Modal */}
            {invoiceModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        maxWidth: '1400px',
                        width: '95%',
                        maxHeight: '95vh',
                        overflow: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                    }}>
                        {/* Header - Nova Compra (Fatura Fornecedor) */}
                        <div style={{
                            padding: '1.5rem 2rem',
                            borderBottom: '1px solid var(--border-color)',
                            background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)', fontWeight: '600' }}>
                                {newInvoice ? '📝 Nova Compra (Fatura Fornecedor)' : `📄 Fatura ${invoiceDetails?.invoiceNumber || ''}`}
                            </h2>
                        </div>

                        <div style={{ padding: '2rem' }}>
                            {invoiceLoading ? (
                                <div style={{ padding: '3rem', textAlign: 'center' }}>
                                    <div style={{ fontSize: '2rem' }}>⏳</div>
                                    <p>A carregar detalhes...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Form Header - Like first image */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(4, 1fr)',
                                        gap: '1rem',
                                        marginBottom: '1.5rem'
                                    }}>
                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Código Fornecedor *
                                            </label>
                                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Código..."
                                                    disabled={!editMode}
                                                    value={invoiceDetails?.supplier?.code || ''}
                                                    style={{
                                                        flex: 1,
                                                        padding: '0.5rem',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: '4px',
                                                        fontSize: '0.875rem',
                                                        backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                    }}
                                                />
                                                {editMode && (
                                                    <button style={{
                                                        padding: '0.5rem 0.75rem',
                                                        background: 'var(--info)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}>
                                                        🔍
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Nome Fornecedor
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Selecione pelo código"
                                                disabled={!editMode}
                                                value={invoiceDetails?.supplier?.name || ''}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Nº Fatura
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="AUTOMÁTICO"
                                                disabled={!editMode || !newInvoice}
                                                value={invoiceDetails?.invoiceNumber || 'AUTOMÁTICO'}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, invoiceNumber: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: (editMode && newInvoice) ? '#fff' : '#f5f5f5',
                                                    fontWeight: '600'
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Data Fatura
                                            </label>
                                            <input
                                                type="date"
                                                disabled={!editMode}
                                                value={invoiceDetails?.invoiceDate?.split('T')[0] || new Date().toISOString().split('T')[0]}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, invoiceDate: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Data Vencimento
                                            </label>
                                            <input
                                                type="date"
                                                placeholder="dd / mm / aaaa"
                                                disabled={!editMode}
                                                value={invoiceDetails?.dueDate?.split('T')[0] || ''}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, dueDate: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Ref. Fornecedor
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Ref externa"
                                                disabled={!editMode}
                                                value={invoiceDetails?.supplierReference || ''}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, supplierReference: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Armazém
                                            </label>
                                            <select
                                                disabled={!editMode}
                                                value={invoiceDetails?.warehouseId || ''}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, warehouseId: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                }}
                                            >
                                                <option value="">Selecione...</option>
                                                <option value="1">Armazém Principal</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Método Pag.
                                            </label>
                                            <select
                                                disabled={!editMode}
                                                value={invoiceDetails?.paymentMethod || ''}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, paymentMethod: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5'
                                                }}
                                            >
                                                <option value="">Selecione...</option>
                                                <option value="cash">Dinheiro</option>
                                                <option value="card">Cartão</option>
                                                <option value="transfer">Transferência</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ 
                                                fontSize: '0.7rem', 
                                                color: 'var(--text-secondary)', 
                                                fontWeight: '700', 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                textTransform: 'uppercase'
                                            }}>
                                                Desc. Global %
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="0"
                                                disabled={!editMode}
                                                value={invoiceDetails?.globalDiscountPercent || 0}
                                                onChange={(e) => setInvoiceDetails({...invoiceDetails, globalDiscountPercent: e.target.value})}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: editMode ? '#fff' : '#f5f5f5',
                                                    textAlign: 'right'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Checkbox - Atualizar stock */}
                                    {editMode && (
                                        <div style={{ marginBottom: '1rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                                                <input type="checkbox" />
                                                Atualizar stock automaticamente ao lançar
                                            </label>
                                        </div>
                                    )}

                                    {/* NOTAS */}
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <label style={{ 
                                            fontSize: '0.7rem', 
                                            color: 'var(--text-secondary)', 
                                            fontWeight: '700', 
                                            display: 'block', 
                                            marginBottom: '0.25rem',
                                            textTransform: 'uppercase'
                                        }}>
                                            Notas
                                        </label>
                                        <textarea
                                            disabled={!editMode}
                                            value={invoiceDetails?.notes || ''}
                                            onChange={(e) => setInvoiceDetails({...invoiceDetails, notes: e.target.value})}
                                            rows={3}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '4px',
                                                fontSize: '0.875rem',
                                                backgroundColor: editMode ? '#fff' : '#f5f5f5',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>

                                    {/* Linhas da Compra */}
                                    <div style={{
                                        marginBottom: '1.5rem',
                                        padding: '1rem',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '8px',
                                        backgroundColor: 'var(--bg-card)'
                                    }}>
                                        <h3 style={{ 
                                            margin: '0 0 1rem 0', 
                                            fontSize: '0.9rem', 
                                            fontWeight: '700',
                                            color: 'var(--text-secondary)',
                                            textTransform: 'uppercase'
                                        }}>
                                            Linhas da Compra
                                        </h3>

                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Referência</th>
                                                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Descrição</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>QTD</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>P.Unit.</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Desc%</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Taxa IVA</th>
                                                        <th style={{ padding: '10px', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total</th>
                                                        {editMode && <th style={{ padding: '10px', width: '50px' }}></th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoiceDetails?.lines?.map((line, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                            <td style={{ padding: '10px', fontSize: '0.875rem' }}>
                                                                {line.article?.reference || '-'}
                                                            </td>
                                                            <td style={{ padding: '10px', fontSize: '0.875rem' }}>
                                                                {line.article?.name || line.description}
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right', fontSize: '0.875rem' }}>
                                                                {line.quantity}
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right', fontSize: '0.875rem' }}>
                                                                {formatCurrency(line.unitPrice)}
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right', fontSize: '0.875rem' }}>
                                                                {line.discountPercent}%
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right', fontSize: '0.875rem' }}>
                                                                {line.vatPercent}%
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600' }}>
                                                                {formatCurrency(line.lineTotal)}
                                                            </td>
                                                            {editMode && (
                                                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                                                    <button style={{
                                                                        background: 'var(--error)',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        padding: '0.25rem 0.5rem',
                                                                        cursor: 'pointer',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        🗑️
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                    {(!invoiceDetails?.lines || invoiceDetails.lines.length === 0) && (
                                                        <tr>
                                                            <td colSpan={editMode ? 8 : 7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                                                Nº linhas: 0
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {editMode && (
                                            <button style={{
                                                marginTop: '1rem',
                                                padding: '0.5rem 1rem',
                                                background: 'var(--bg-card)',
                                                color: 'var(--info)',
                                                border: '1px solid var(--info)',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                fontWeight: '600'
                                            }}>
                                                + Linha
                                            </button>
                                        )}
                                    </div>

                                    {/* Totals */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        marginBottom: '1.5rem'
                                    }}>
                                        <div style={{ minWidth: '250px' }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                padding: '0.5rem 0',
                                                fontSize: '0.875rem'
                                            }}>
                                                <span>Subtotal:</span>
                                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoiceDetails?.subTotal || 0)}</span>
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                padding: '0.5rem 0',
                                                fontSize: '0.875rem'
                                            }}>
                                                <span>IVA:</span>
                                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoiceDetails?.taxTotal || 0)}</span>
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '0.75rem 0',
                                                borderTop: '2px solid var(--border-color)',
                                                marginTop: '0.5rem',
                                                fontSize: '1rem',
                                                fontWeight: '700'
                                            }}>
                                                <span>Total:</span>
                                                <span style={{ color: 'var(--success)' }}>{formatCurrency(invoiceDetails?.total || 0)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons at Bottom - Like in image */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '1rem',
                                        justifyContent: 'flex-end',
                                        paddingTop: '1.5rem',
                                        borderTop: '1px solid var(--border-color)'
                                    }}>
                                        {editMode && (
                                            <button
                                                onClick={saveInvoice}
                                                style={{
                                                    padding: '0.75rem 2rem',
                                                    background: 'var(--success)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '1rem',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}
                                            >
                                                💾 Gravar Documento
                                            </button>
                                        )}
                                        <button
                                            onClick={closeInvoiceModal}
                                            style={{
                                                padding: '0.75rem 2rem',
                                                background: 'var(--bg-tertiary)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '1rem',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Cancelar
                                        </button>
                                        {!editMode && !newInvoice && (
                                            <>
                                                <button
                                                    onClick={toggleEditMode}
                                                    style={{
                                                        padding: '0.75rem 2rem',
                                                        background: 'var(--info)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '1rem',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    ✏️ Editar
                                                </button>
                                                <button
                                                    onClick={duplicateInvoice}
                                                    style={{
                                                        padding: '0.75rem 2rem',
                                                        background: 'var(--info)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '1rem',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    📋 Duplicar
                                                </button>
                                                <button
                                                    onClick={deleteInvoice}
                                                    style={{
                                                        padding: '0.75rem 2rem',
                                                        background: 'var(--error)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '1rem',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    🗑️ Anular
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

const styles = {
    container: {
        minHeight: '100%',
        backgroundColor: 'var(--bg-main)',
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '1rem',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid var(--info)',
        borderTop: '4px solid var(--accent-primary)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    header: {
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        padding: '1.5rem',
    },
    title: {
        margin: 0,
        fontSize: '1.5rem',
        color: 'white',
    },
    subtitle: {
        margin: '0.25rem 0 1rem 0',
        opacity: 0.8,
    },
    periodSelector: {
        display: 'flex',
        gap: '0.5rem',
    },
    periodBtn: {
        padding: '0.5rem 1rem',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        color: 'white',
        cursor: 'pointer',
        fontSize: '0.875rem',
    },
    periodBtnActive: {
        backgroundColor: 'white',
        color: 'var(--info-text)',
    },
    nav: {
        display: 'flex',
        backgroundColor: 'white',
        borderBottom: '1px solid var(--info)',
        overflowX: 'auto',
    },
    navBtn: {
        flex: 1,
        padding: '1rem 0.5rem',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.75rem',
        color: 'var(--info)',
        borderBottom: '2px solid transparent',
        minWidth: '70px',
    },
    navBtnActive: {
        color: 'var(--accent-primary)',
        borderBottomColor: 'var(--accent-primary)',
    },
    content: {
        padding: '1rem',
    },
    sectionTitle: {
        fontSize: '1.25rem',
        fontWeight: '600',
        marginBottom: '1rem',
    },
    kpiGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
        marginBottom: '1.5rem',
    },
    kpiCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        display: 'flex',
        gap: '0.75rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    kpiIcon: {
        fontSize: '1.5rem',
    },
    kpiInfo: {
        display: 'flex',
        flexDirection: 'column',
    },
    kpiLabel: {
        fontSize: '0.75rem',
        color: 'var(--info)',
    },
    kpiValue: {
        fontSize: '1.5rem',
        fontWeight: '700',
    },
    kpiChange: {
        fontSize: '0.75rem',
        color: 'var(--success-text)',
    },
    chartsRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '1.5rem',
    },
    chartCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '1rem',
    },
    chartPlaceholder: {
        height: '200px',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '1rem 0',
    },
    barChart: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '0.5rem',
        height: '100%',
        width: '100%',
    },
    barItem: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
    },
    bar: {
        width: '100%',
        backgroundColor: 'var(--accent-primary)',
        borderRadius: '4px 4px 0 0',
        minHeight: '10px',
    },
    timeHeatmap: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        justifyContent: 'center',
    },
    heatCell: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: '600',
        fontSize: '0.875rem',
    },
    quickStats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.5rem',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    quickStatItem: {
        textAlign: 'center',
    },
    quickStatValue: {
        display: 'block',
        fontSize: '1.25rem',
        fontWeight: '700',
        color: 'var(--accent-primary)',
    },
    quickStatLabel: {
        fontSize: '0.625rem',
        color: 'var(--info)',
    },
    membersGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
    },
    memberCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    bigNumber: {
        fontSize: '2rem',
        fontWeight: '700',
        color: 'var(--accent-primary)',
    },
    segmentList: {
        marginTop: '1rem',
    },
    segment: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--info)',
    },
    segmentDot: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
    },
    segmentValue: {
        marginLeft: 'auto',
        fontWeight: '600',
    },
    atRiskList: {
        marginTop: '1rem',
    },
    atRiskItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--info)',
    },
    atRiskInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    riskBadge: {
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
    },
    contactBtn: {
        padding: '0.375rem 0.75rem',
        backgroundColor: 'var(--accent-primary)',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.75rem',
    },
    classesStats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
    },
    classStat: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    topClassesList: {
        marginTop: '1rem',
    },
    topClassItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--info)',
    },
    classRank: {
        fontWeight: '700',
        color: 'var(--accent-primary)',
    },
    classInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    classProgress: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        width: '100px',
    },
    classProgressBar: {
        flex: 1,
        height: '6px',
        backgroundColor: 'var(--info-bg)',
        borderRadius: '3px',
        overflow: 'hidden',
    },
    classProgressFill: {
        height: '100%',
        backgroundColor: 'var(--success)',
    },
    revenueGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
    },
    revenueCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    revenueBig: {
        fontSize: '1.25rem',
        fontWeight: '700',
        color: 'var(--success)',
        margin: '0.5rem 0',
    },
    revenueChange: {
        fontSize: '0.75rem',
        color: 'var(--info)',
    },
    revenueBreakdown: {
        marginTop: '1rem',
    },
    breakdownItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--info)',
    },
    breakdownInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    breakdownDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
    },
    breakdownValue: {
        fontWeight: '600',
    },
    engagementGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
    },
    engagementCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    engagementValue: {
        display: 'block',
        fontSize: '1.25rem',
        fontWeight: '700',
        color: 'var(--accent-primary)',
    },
    badgesList: {
        marginTop: '1rem',
    },
    badgeItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--info)',
    },
    badgeIcon: {
        fontSize: '1.5rem',
    },
    badgeName: {
        flex: 1,
    },
    badgeCount: {
        fontWeight: '600',
        color: 'var(--accent-primary)',
    },
    appStats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
        marginTop: '1rem',
    },
    appStatItem: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0.75rem',
        backgroundColor: 'var(--info-bg)',
        borderRadius: '8px',
    },
    exportSection: {
        padding: '1rem',
    },
    exportBtn: {
        width: '100%',
        padding: '1rem',
        backgroundColor: 'var(--info)',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontSize: '1rem',
        cursor: 'pointer',
    },
    card: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
};

export default AdminAdvancedDashboard;




